import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute } from "@tanstack/react-router";
import { HelpfulPricingGuides } from "@/components/HelpfulPricingGuides";
import { Link } from "@tanstack/react-router";
import { Phone, CheckCircle, ArrowRight, Settings, Gauge, Wrench as WrenchIcon } from "lucide-react";
import { services, getBusinessConfig } from "@/lib/site-config";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { HelpfulGuidesGrid } from "@/components/HelpfulGuidesGrid";
import { ServiceHero } from "@/components/ServiceHero";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { ProcessSteps } from "@/components/ProcessSteps";
import { CTABlock } from "@/components/CTABlock";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { InternalLinksBlock } from "@/components/InternalLinksBlock";
import { DriverSupportCTA } from "@/components/DriverSupportCTA";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";

const svc = services[1]; // servicing

const tiers = [
  {
    name: "Interim Service",
    desc: "Essential maintenance for vehicles doing high mileage or heavy urban driving. Covers the basics to keep things running smoothly between full services.",
    includes: ["Oil and filter change", "Brake inspection", "Tyre check and pressures", "Fluid top-ups", "Visual safety check"],
  },
  {
    name: "Full Service",
    desc: "Comprehensive annual service covering all major systems. Recommended every 12 months or 12,000 miles to maintain reliability and protect your warranty.",
    includes: ["Everything in interim", "Air filter replacement", "Cabin/pollen filter", "Spark plug inspection", "Battery health check", "Full vehicle health report"],
  },
  {
    name: "Major Service",
    desc: "The most thorough service level. Ideal at key mileage milestones or for vehicles that haven't been serviced in a while. Covers everything plus deeper checks.",
    includes: ["Everything in full", "Transmission fluid check", "Coolant system flush", "Brake fluid replacement", "Detailed engine inspection", "Service book stamped"],
  },
];

const extendedFaqs = [
  ...svc.faqs,
  { q: "What happens if you find an issue during my service?", a: "We'll contact you before doing any additional work. You'll get a clear explanation and a quote — no work starts without your approval." },
  { q: "Is regular servicing really necessary?", a: "Yes. Regular servicing catches problems early, keeps your engine efficient, maintains your warranty, and helps your car hold its value. Skipping services often leads to more expensive repairs later." },
  { q: "How much does a service cost?", a: "Interim service from £129, full service from £179, major service from £249. Prices include OE-quality oil and filters; we'll confirm the exact figure for your make and model when you book." },
  { q: "How long does a service take?", a: "Interim service: 1–2 hours. Full service: 3–4 hours. Major service: usually a full day. Most cars are booked in as drop-off so you don't have to wait, but we can stage shorter slots if you need to wait in." },
  { q: "What's included in a full service?", a: "Engine oil and filter change, air filter, cabin filter, spark plugs (on petrol cars), brake fluid check, coolant check, suspension and steering inspection, tyre and brake-pad measurement, lights and wipers, and a full visual safety check. You get a written report and your service book stamped." },
];


export const Route = createFileRoute("/servicing")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) => seoHead({
    path: "/servicing",
    title: "Car Servicing East London E14 | Honest Garage | Woodseer",
    description: `Interim, full and major car servicing in East London E14. Service book stamped, OE-quality parts, written quote first. Call Woodseer Autos: ${NAP.phone}.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Car Servicing", path: "/servicing" },
    ],
    jsonLd: [
      localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
      faqSchema(extendedFaqs),
      serviceSchema({
        name: "Car Servicing — Woodseer Autos",
        serviceType: "Car Servicing",
        description: "Interim, full and major car servicing in East London. All makes and models with service book stamped.",
        offers: { price: "129.00", priceCurrency: "GBP", description: "Interim service from £129; full service from £179; major service from £249." },
      }),
    ],
  }),
  component: ServicingPage,
});

function ServicingPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <ServiceHero
        headline={svc.headline}
        intro={svc.intro}
        image="/images/hero-servicing.jpg"
        imageAlt="Car servicing at Woodseer Autos, East London"
      />

      {/* Book now strip */}
      <section className="bg-primary/10 border-y border-primary/20">
        <div className="mx-auto max-w-5xl px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm font-medium text-foreground text-center sm:text-left">Service now, avoid bigger bills later. Book your car in today.</p>
          <div className="flex flex-wrap items-center justify-center gap-2 shrink-0">
            <PrimaryCTA location="servicing-book-strip" intent="service quote" variant="light" />
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      {/* Service Tiers */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Choose Your Service Level</h2>
            <p className="mt-3 text-center text-muted-foreground">Not sure which you need? Call us and we'll advise based on your car's age, mileage and service history.</p>
            <StaggerContainer className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {tiers.map((tier) => (
                <StaggerItem key={tier.name}>
                  <div className="flex flex-col rounded-xl border border-border bg-background p-6 h-full">
                    <h3 className="text-lg font-bold text-foreground">{tier.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{tier.desc}</p>
                    <ul className="mt-4 space-y-2 flex-1">
                      {tier.includes.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <a href={biz.phoneHref} className="mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
                      <Phone className="h-4 w-4" />Book This Service
                    </a>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Why Regular Servicing Matters */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Why Regular Servicing Matters</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {[
                "Catches problems early before they become expensive",
                "Keeps your engine running efficiently — better fuel economy",
                "Maintains your manufacturer warranty",
                "Helps your car hold its resale value",
                "Keeps you safe — brakes, tyres and steering checked",
                "Peace of mind that your vehicle is reliable",
              ].map((benefit) => (
                <StaggerItem key={benefit}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {benefit}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Signs you're overdue */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Signs Your Car Is Overdue a Service</h2>
            <ul className="mt-8 space-y-3 max-w-xl mx-auto">
              {svc.signs.map((sign) => (
                <li key={sign} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="text-primary font-bold">→</span>
                  {sign}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </AnimatedSection>

      {/* Why choose us */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Why Choose Woodseer Autos</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {svc.whyUs.map((reason) => (
                <StaggerItem key={reason}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {reason}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <ProcessSteps steps={svc.process} title="How Servicing Works" />
      </AnimatedSection>

      <AnimatedSection>
      <HelpfulGuidesGrid slugs={["mot-warning-lights-before-test","selling-car-repair-checklist","tyre-check-before-mot"]} title="Helpful guides for this service" compact />

        <FAQAccordion faqs={extendedFaqs} />
      </AnimatedSection>

      {/* Related services */}
      <AnimatedSection>
        <ServiceTestimonials
          service="Servicing"
          title="What service customers say"
          itemReviewedType="Service"
          itemReviewedName="Car Servicing — Woodseer Autos"
          serviceType="Car Servicing"
        />
      </AnimatedSection>

      <AnimatedSection>
        <InternalLinksBlock currentService="servicing" serviceForAreaLinks="servicing" />
      </AnimatedSection>

      <HelpfulPricingGuides currentService="servicing" />
      <div className="mx-auto max-w-4xl px-4"><DriverSupportCTA page="servicing" service="servicing" bookingContext="servicing-driver-support" /></div>
      <WhyChooseWoodseer page="servicing" />
      <CTABlock headline="Book Your Service Today" subtext="Call us to arrange your service. We'll advise on the right level for your car and get you booked in fast." />
    </>
  );
}
